import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, CheckCircle2, AlertCircle, ArrowRight, Loader2, Shield, Calendar, Target, History } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SharedProgressPage() {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const response = await axios.get(`${API}/api/client-progress/shared/${shareId}`);
        if (response.data.success) {
          setProgress(response.data.progress);
          setHistory(response.data.history || []);
        } else {
          setError(response.data.error || 'Failed to load progress report');
        }
      } catch (err) {
        console.error('Error fetching progress:', err);
        if (err.response?.status === 404) {
          setError('This progress report was not found.');
        } else if (err.response?.status === 410) {
          setError('This progress report has expired.');
        } else {
          setError('Failed to load progress report. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (shareId) {
      fetchProgress();
    }
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-400 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading your progress report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-red-500/30 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Unable to Load Report</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  const analysis = progress?.analysis || {};
  const scores = analysis.scores || {};
  const updates = analysis.updates || [];
  
  const isMultiBureau = (val) => val && typeof val === 'object' && !Array.isArray(val);

  // Calculate score journey from history
  const getScoreJourney = () => {
    if (history.length < 2) return null;
    
    const firstReport = history[0];
    const currentReport = history[history.length - 1];
    
    const firstScores = firstReport?.analysis?.scores?.current;
    const currentScores = currentReport?.analysis?.scores?.current;
    
    if (!firstScores || !currentScores) return null;
    
    const journey = {};
    
    if (isMultiBureau(currentScores) && isMultiBureau(firstScores)) {
      Object.keys(currentScores).forEach(bureau => {
        const start = firstScores[bureau] || 0;
        const current = currentScores[bureau] || 0;
        journey[bureau] = {
          start,
          current,
          change: current - start
        };
      });
    } else if (currentScores && firstScores) {
      journey['Score'] = {
        start: firstScores,
        current: currentScores,
        change: currentScores - firstScores
      };
    }
    
    return Object.keys(journey).length > 0 ? journey : null;
  };

  const scoreJourney = getScoreJourney();

  const renderScoreCards = () => {
    const currentScore = scores.current;
    const changeScore = scores.change;
    
    if (isMultiBureau(currentScore)) {
      const bureaus = Object.keys(currentScore);
      return (
        <div className={`grid grid-cols-1 ${bureaus.length > 1 ? 'md:grid-cols-2' : ''} gap-4`}>
          {bureaus.map((bureau) => {
            const current = currentScore[bureau];
            const change = isMultiBureau(changeScore) ? changeScore[bureau] : null;
            
            return (
              <div key={bureau} className="bg-zinc-800 rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-2 text-center">{bureau}</p>
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-white">{current}</p>
                  </div>
                  {change !== null && change !== undefined && (
                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                      change > 0 ? 'bg-green-500/20 text-green-400' 
                        : change < 0 ? 'bg-red-500/20 text-red-400' 
                        : 'bg-zinc-700 text-zinc-400'
                    }`}>
                      {change > 0 ? <TrendingUp className="w-4 h-4" />
                        : change < 0 ? <TrendingDown className="w-4 h-4" />
                        : <Minus className="w-4 h-4" />}
                      <span className="font-bold">{change > 0 ? '+' : ''}{change}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    
    if (currentScore) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {scores.previous && (
            <div className="text-center p-4 bg-zinc-800 rounded-lg">
              <p className="text-xs text-zinc-400 mb-1">Previous Score</p>
              <p className="text-3xl font-bold text-zinc-300">{scores.previous}</p>
            </div>
          )}
          <div className="text-center p-4 bg-zinc-800 rounded-lg">
            <p className="text-xs text-zinc-400 mb-1">Current Score</p>
            <p className="text-3xl font-bold text-white">{currentScore}</p>
          </div>
          {changeScore !== undefined && changeScore !== null && typeof changeScore === 'number' && (
            <div className={`text-center p-4 rounded-lg ${
              changeScore > 0 ? 'bg-green-500/20 border border-green-500/30' 
                : changeScore < 0 ? 'bg-red-500/20 border border-red-500/30' 
                : 'bg-zinc-800'
            }`}>
              <p className="text-xs text-zinc-400 mb-1">Change</p>
              <div className={`text-3xl font-bold flex items-center justify-center gap-2 ${
                changeScore > 0 ? 'text-green-400' : changeScore < 0 ? 'text-red-400' : 'text-zinc-300'
              }`}>
                {changeScore > 0 ? <TrendingUp className="w-6 h-6" />
                  : changeScore < 0 ? <TrendingDown className="w-6 h-6" />
                  : <Minus className="w-6 h-6" />}
                {changeScore > 0 ? '+' : ''}{changeScore}
              </div>
              <p className="text-xs text-zinc-500 mt-1">points</p>
            </div>
          )}
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Credit Progress Update</h1>
          <p className="text-zinc-400 mt-2">
            {progress?.client_name ? `For ${progress.client_name}` : 'Your personalized credit report analysis'}
          </p>
          {progress?.created_at && (
            <p className="text-zinc-500 text-sm mt-2 flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(progress.created_at).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric'
              })}
            </p>
          )}
        </div>

        {/* Score Journey Tracker */}
        {scoreJourney && history.length > 1 && (
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-purple-400 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Your Credit Score Journey
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(scoreJourney).map(([bureau, data]) => (
                <div key={bureau} className="bg-zinc-900/50 rounded-lg p-4">
                  <p className="text-sm text-zinc-400 mb-3 font-medium">{bureau}</p>
                  <div className="flex items-center justify-between">
                    <div className="text-center">
                      <p className="text-xs text-zinc-500">Started</p>
                      <p className="text-2xl font-bold text-zinc-400">{data.start}</p>
                    </div>
                    <div className="flex-1 px-4">
                      <div className="h-2 bg-zinc-700 rounded-full relative overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${data.change >= 0 ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(100, Math.abs(data.change) / 2)}%` }}
                        />
                      </div>
                      <div className="flex justify-center mt-1">
                        <ArrowRight className="w-4 h-4 text-zinc-500" />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-zinc-500">Now</p>
                      <p className="text-2xl font-bold text-white">{data.current}</p>
                    </div>
                  </div>
                  <div className={`text-center mt-3 py-2 rounded-lg ${
                    data.change > 0 ? 'bg-green-500/20 text-green-400' 
                      : data.change < 0 ? 'bg-red-500/20 text-red-400' 
                      : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    <span className="font-bold text-lg">
                      {data.change > 0 ? '+' : ''}{data.change} points
                    </span>
                    {data.change > 0 && <span className="ml-2">🎉</span>}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 text-center mt-4">
              Based on {history.length} progress update{history.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Current Score Summary */}
        {(scores.current || scores.previous) && (
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-teal-400" />
              Current Credit Scores
            </h2>
            {renderScoreCards()}
            {scores.bureau && typeof scores.bureau === 'string' && (
              <p className="text-center text-xs text-zinc-500 mt-4">Data from {scores.bureau}</p>
            )}
          </div>
        )}

        {/* What This Means */}
        {analysis.explanation && (
          <div className="bg-gradient-to-br from-teal-500/10 to-emerald-500/10 border border-teal-500/30 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-teal-400 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              What This Means For You
            </h2>
            <div className="text-zinc-200 whitespace-pre-wrap leading-relaxed">{analysis.explanation}</div>
          </div>
        )}

        {/* Account Updates */}
        {updates.length > 0 && (
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Account Updates</h2>
            <div className="space-y-3">
              {updates.map((update, index) => (
                <div key={index} className={`p-4 rounded-lg flex items-start gap-3 ${
                  update.type === 'positive' ? 'bg-green-500/10 border border-green-500/30' 
                    : update.type === 'negative' ? 'bg-red-500/10 border border-red-500/30'
                    : 'bg-zinc-800 border border-zinc-700'
                }`}>
                  {update.type === 'positive' ? <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    : update.type === 'negative' ? <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    : <ArrowRight className="w-5 h-5 text-zinc-400 flex-shrink-0 mt-0.5" />}
                  <div>
                    <p className={`font-medium ${
                      update.type === 'positive' ? 'text-green-400' : update.type === 'negative' ? 'text-red-400' : 'text-white'
                    }`}>{update.title}</p>
                    <p className="text-sm text-zinc-400 mt-1">{update.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Update History Timeline */}
        {history.length > 1 && (
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-purple-400" />
              Your Complete Update History
            </h2>
            <div className="space-y-4">
              {history.slice().reverse().map((report, index) => {
                const isCurrentReport = report.share_id === shareId;
                const reportScores = report.analysis?.scores?.current;
                const reportUpdates = report.analysis?.updates || [];
                const positiveCount = reportUpdates.filter(u => u.type === 'positive').length;
                
                return (
                  <div 
                    key={report.share_id} 
                    className={`p-4 rounded-lg border ${
                      isCurrentReport 
                        ? 'bg-teal-500/10 border-teal-500/30' 
                        : 'bg-zinc-800/50 border-zinc-700/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-zinc-500" />
                        <span className="text-sm font-medium text-zinc-300">
                          {new Date(report.created_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric'
                          })}
                        </span>
                        {isCurrentReport && (
                          <span className="text-xs bg-teal-500/20 text-teal-400 px-2 py-0.5 rounded">Current</span>
                        )}
                      </div>
                      {positiveCount > 0 && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                          {positiveCount} positive
                        </span>
                      )}
                    </div>
                    {reportScores && (
                      <p className="text-sm text-zinc-400">
                        Scores: {isMultiBureau(reportScores) 
                          ? Object.entries(reportScores).map(([b, s]) => `${b}: ${s}`).join(', ')
                          : reportScores
                        }
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6 border-t border-zinc-800">
          <p className="text-zinc-500 text-sm">Generated by Clear All Credit Repair</p>
          <p className="text-zinc-600 text-xs mt-2">Questions? Contact your credit repair specialist.</p>
        </div>
      </div>
    </div>
  );
}
